import axios, { type AxiosInstance } from 'axios';

import { publishToast } from '../components/feedback/toastBus';
import i18next from '../i18n';

/**
 * Cross-cutting response interceptors layered on top of the existing
 * `apiClient` (client.ts owns the 401 → refresh-and-retry path; this
 * module owns the *post-refresh* user-facing signals).
 *
 * Responsibilities (ADMIN-10 / PLAN §3 + §4.4 + §4.15):
 *   - **403** (`insufficient_permissions`) → toast "no access" so an
 *     admin whose permission was revoked mid-session (15-min JWT
 *     staleness per §4.4) gets a clear signal instead of a silent fail.
 *   - **409** (stale-state / optimistic-concurrency conflict, e.g. two
 *     admins acting on the same entity per §4.1 / §4.9 / §4.15) → toast
 *     "another admin already acted — refresh".
 *
 * The interceptor only *signals* — it re-rejects the error so the
 * calling mutation's own `onError` still runs (e.g. TanStack invalidate).
 * Toasts carry pre-translated text (the bus never carries raw keys).
 *
 * Idempotent: `installResponseInterceptors` guards against double-install
 * (HMR in dev, repeated calls in tests).
 *
 * **Per-request opt-out (`skipAutoErrorToast`, AUT-223).** The blanket 409
 * toast is right when a conflict means nothing more specific than "someone
 * beat you to it". It is WRONG on the capture endpoints, where the 409 `code`
 * is the operator's next instruction — `payment_capture_already_claimed`
 * means the money already moved and a retry would be a mistake, while
 * `payment_not_capturable` just means refresh. Firing "Другой администратор
 * уже изменил…" next to that specific message trains people to ignore both.
 * So callers whose own handler renders the server's reason opt out here, and
 * only there. Default behaviour is unchanged.
 */
const INSTALLED = Symbol.for('carcheck.admin.interceptorsInstalled');

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Suppress the blanket 403 / 409 toast for THIS request because the
     * caller surfaces the server's own `code` / `message` itself.
     */
    skipAutoErrorToast?: boolean;
  }
}

type Marked = AxiosInstance & { [INSTALLED]?: boolean };

const t = (key: string): string => i18next.t(key);

export const installResponseInterceptors = (client: AxiosInstance): void => {
  const marked = client as Marked;
  if (marked[INSTALLED]) return;
  marked[INSTALLED] = true;

  client.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (
        axios.isAxiosError(error) &&
        error.response &&
        error.config?.skipAutoErrorToast !== true
      ) {
        const { status } = error.response;
        if (status === 403) {
          publishToast({
            id: 'http-403',
            message: t('errors.forbidden'),
            severity: 'error',
            durationMs: 6000,
          });
        } else if (status === 409) {
          publishToast({
            id: 'http-409',
            message: t('errors.conflict'),
            severity: 'warning',
            durationMs: 8000,
          });
        }
      }
      return Promise.reject(error);
    },
  );
};

/** Test-only: clear the install marker so a fresh client can be wired. */
export const _resetInterceptorInstallForTests = (client: AxiosInstance): void => {
  (client as Marked)[INSTALLED] = false;
};
